import * as React from "react";
import { useMemo, useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
  Sector,
} from "recharts";
import {
  TrendingUp,
  PieChart as PieIcon,
  BarChart as BarIcon,
  LineChart as LineIcon,
} from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "../ui/button";
import { Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";
import toast from "react-hot-toast";

// Reuse the existing color generation function
const generateColors = (numColors) => {
  const baseHues = [210, 330, 150, 270, 30, 180, 0, 240, 60, 300];
  const colors = [];
  const saturation = 65;
  const lightness = 55;

  for (let i = 0; i < numColors; i++) {
    const currentHue = baseHues[i % baseHues.length];
    const adjustedSaturation =
      saturation + Math.floor(i / baseHues.length) * 10;
    const adjustedLightness = lightness - Math.floor(i / baseHues.length) * 5;
    colors.push(
      `hsl(${currentHue}, ${adjustedSaturation}%, ${adjustedLightness}%)`
    );
  }
  return colors;
};

const exportToExcel = (data, title, nameKey, valueKey) => {
  if (!data || data.length === 0) {
    toast.error("No data to export.");
    return;
  }
  // Prepare data for export
  const exportData = data.map((item) => {
    const row = {
      [nameKey]: item[nameKey],
      [valueKey]: item[valueKey],
    };
    if (item.cumulativeValue !== undefined) {
      row["Cumulative Value"] = item.cumulativeValue;
    }
    return row;
  });

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  // Add title row
  XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });

  // Adjust column widths
  const maxWidth = Math.max(
    ...exportData.map((row) => String(row[nameKey]).length)
  );
  ws["!cols"] = [
    { wch: maxWidth }, // Name column
    { wch: 12 }, // Value column
    { wch: 15 }, // Cumulative value column (if exists)
  ];

  // Add the worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "Data");

  // Generate Excel file and trigger download
  XLSX.writeFile(
    wb,
    `${title.toLowerCase().replace(/\s+/g, "_")}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`
  );
};

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
    props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 6}
        outerRadius={outerRadius + 10}
        fill={fill}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, nameKey, valueKey, type }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  if (type === "line") {
    return (
      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-lg border max-w-xs sm:max-w-sm">
        <p className="font-medium text-sm sm:text-base break-words">
          {data[nameKey]}
        </p>
        <div className="space-y-1 mt-2">
          <p className="text-xs sm:text-sm">
            Value: {data[valueKey].toLocaleString()}
          </p>
          <p className="text-xs sm:text-sm text-blue-600">
            Total Product Sales: {data.cumulativeValue.toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-3 sm:p-4 rounded-lg shadow-lg border max-w-xs sm:max-w-sm">
      <p className="font-medium text-sm sm:text-base break-words">
        {data[nameKey]}
      </p>
      <p className="text-xs sm:text-sm">
        Value: {data[valueKey].toLocaleString()}
      </p>
    </div>
  );
};

const ChartViewTemplate = ({
  title,
  description,
  data,
  valueKey = "value",
  nameKey = "name",
  customColors,
  footerTrend,
  footerText,
  hidePieChart = false, // New prop to hide pie chart
}) => {
  const [selectedOthers, setSelectedOthers] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const colors = useMemo(() => {
    if (customColors) return customColors;
    const dataLength = Array.isArray(data)
      ? data.length
      : Object.keys(data).length;
    return generateColors(dataLength);
  }, [data, customColors]);

  // this function first checks whether we have an array of objects or simply an object
  // bc this comp is being used as a standalone comp as well in some places
  // the only function of this func is to simply add the color along with each row
  // so if we two rows, it's going to dynamically as the color, let's say, "green" to the first row
  // and "orange" to the second row. That way, they will displayed with different colors inside the chart and
  // it will be easy to differentiate between the two
  const transformedData = useMemo(() => {
    if (!Array.isArray(data)) {
      return Object.entries(data).map(([name, value], index) => ({
        [nameKey]: name,
        [valueKey]: value,
        fill: colors[index],
      }));
    }
    return data.map((item, index) => ({
      ...item,
      fill: colors[index],
    }));
  }, [data, nameKey, valueKey, colors]);

  // this function calculates the "total amount", when a single metric is selected
  // the data rows are in format [{ad_spend: 1,...}, {ad_spend: 2,...}]
  // the "valueKey" we receive when a single metric is selected is its keyname inside the object,
  // so for example, here we receive valueKey = "ad_spend", if purchase was selected, it would be valueKey = "purchase"
  const total = useMemo(() => {
    return transformedData.reduce((acc, curr) => acc + curr[valueKey], 0);
  }, [transformedData, valueKey])?.toFixed(2);

  // pretty self explanatory
  const shouldShowLabels = useMemo(
    () => transformedData.length <= 20,
    [transformedData]
  );

  // this function is only sorting the data for all charts other than "pie"
  const processedData = useMemo(() => {
    // const sortedData = [...transformedData].sort(
    //   (a, b) => b[valueKey] - a[valueKey]
    // );
    return [...transformedData];
  }, [transformedData, nameKey, valueKey, colors]);

  // piechart data is calculated separately because it's not the easiest to manage labels
  // won't need this function once I figure out a way to properly render massive amounts of data and their corresponding labels

  const lineChartData = useMemo(() => {
    let cumulative = 0;
    return processedData.map((item, index) => {
      cumulative += item[valueKey];
      return {
        ...item,
        cumulativeValue: cumulative,
      };
    });
  }, [processedData, valueKey]);

  const pieChartData = useMemo(() => {
    if (transformedData.length <= 20) return transformedData;

    const topItems = transformedData.slice(0, 19);
    const otherItems = transformedData.slice(19);
    const othersSum = otherItems.reduce((sum, item) => sum + item[valueKey], 0);

    return [
      ...topItems,
      {
        [nameKey]: `Others (${otherItems.length} items)`,
        [valueKey]: othersSum,
        fill: colors[19],
        isOthers: true,
        othersData: otherItems.map((item) => ({
          name: item[nameKey],
          value: item[valueKey],
        })),
      },
    ];
  }, [transformedData, nameKey, valueKey, colors]);

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
    const currentSlice = pieChartData[index];
    if (currentSlice.isOthers) {
      setSelectedOthers(currentSlice.othersData);
    }
  };

  const onPieLeave = () => {
    setActiveIndex(-1);
  };

  // Responsive chart dimensions
  const getChartHeight = () => {
    if (typeof window === "undefined") return 400;
    const width = window.innerWidth;
    if (width < 400) return 250; // Very small mobile
    if (width < 640) return 300; // Mobile
    if (width < 768) return 350; // Small tablet
    if (width < 1024) return 400; // Tablet
    if (width < 1440) return 500; // Desktop
    return 600; // Large desktop/4K
  };

  const [chartHeight, setChartHeight] = useState(getChartHeight);

  React.useEffect(() => {
    const handleResize = () => {
      setChartHeight(getChartHeight());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    // using shadcn card comp here, because it looks pretty good
    <Card className="flex flex-col w-full min-w-0">
      <CardHeader className="items-center pb-2 sm:pb-4 px-3 sm:px-6">
        <div className="w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="min-w-0 flex-1 text-center sm:text-left">
            <CardTitle className="text-lg sm:text-xl md:text-2xl break-words">
              {title}
            </CardTitle>
            {description && (
              <CardDescription className="text-sm sm:text-base break-words mt-1">
                {description}
              </CardDescription>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 text-xs sm:text-sm flex-shrink-0 w-full sm:w-auto justify-center sm:justify-start"
            onClick={() =>
              exportToExcel(transformedData, title, nameKey, valueKey)
            }
          >
            <Download className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden xs:inline">Export to Excel</span>
            <span className="xs:hidden">Export</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="w-full p-2 sm:p-3 md:p-6 min-w-0">
        <Tabs
          orientation={
            typeof window !== "undefined" && window.innerWidth >= 768
              ? "vertical"
              : "horizontal"
          }
          className="flex flex-col md:flex-row w-full min-w-0"
          defaultValue={hidePieChart ? "line" : "pie"}
        >
          <TabsList
            className={`w-full ${
              hidePieChart ? "md:w-24" : "md:w-32"
            } flex flex-row md:flex-col h-auto mb-3 sm:mb-4 md:mb-0 grid ${
              hidePieChart ? "grid-cols-2" : "grid-cols-3"
            } md:grid-cols-1 gap-1 md:gap-0 mx-auto sm:mx-0 max-w-sm sm:max-w-none md:max-w-none`}
          >
            {!hidePieChart && (
              <TabsTrigger
                value="pie"
                className="w-full flex gap-1 sm:gap-2 items-center justify-center text-xs sm:text-sm px-2 sm:px-3 py-2"
              >
                <PieIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="hidden xs:inline">Pie Chart</span>
                <span className="xs:hidden">Pie</span>
              </TabsTrigger>
            )}
            <TabsTrigger
              value="bar"
              className="w-full flex gap-1 sm:gap-2 items-center justify-center text-xs sm:text-sm px-2 sm:px-3 py-2"
            >
              <BarIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="hidden xs:inline">Bar Chart</span>
              <span className="xs:hidden">Bar</span>
            </TabsTrigger>
            <TabsTrigger
              value="line"
              className="w-full flex gap-1 sm:gap-2 items-center justify-center text-xs sm:text-sm px-2 sm:px-3 py-2"
            >
              <LineIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="hidden xs:inline">Line Chart</span>
              <span className="xs:hidden">Line</span>
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 md:pl-6 overflow-hidden min-w-0">
            {/* we define the tab content with the value of each chart */}
            {!hidePieChart && (
              <TabsContent value="pie" className="mt-0">
                <div
                  className="overflow-hidden w-full flex items-center justify-center sm:block"
                  style={{ height: `${chartHeight}px` }}
                >
                  <div className="w-full max-w-full h-full flex items-center justify-center sm:block">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart
                        margin={{
                          top: 5,
                          right: 5,
                          bottom: 5,
                          left: 5,
                        }}
                      >
                        <Pie
                          data={pieChartData}
                          dataKey={valueKey}
                          nameKey={nameKey}
                          cx="50%"
                          cy="50%"
                          innerRadius={
                            chartHeight < 350 ? 30 : chartHeight < 400 ? 40 : 60
                          }
                          outerRadius={
                            chartHeight < 350
                              ? 70
                              : chartHeight < 400
                              ? 80
                              : Math.min(140, chartHeight * 0.25)
                          }
                          onMouseEnter={onPieEnter}
                          onMouseLeave={onPieLeave}
                          activeIndex={activeIndex}
                          activeShape={renderActiveShape}
                          label={
                            shouldShowLabels && chartHeight >= 400
                              ? ({ name, percent }) =>
                                  `${name} ${(percent * 100).toFixed(1)}%`
                              : false
                          }
                        >
                          <Label
                            content={({ viewBox }) => (
                              <text
                                x={viewBox.cx}
                                y={viewBox.cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                              >
                                <tspan
                                  x={viewBox.cx}
                                  y={viewBox.cy}
                                  className={`fill-foreground font-bold ${
                                    chartHeight < 350
                                      ? "text-sm"
                                      : chartHeight < 400
                                      ? "text-lg"
                                      : "text-2xl sm:text-3xl"
                                  }`}
                                >
                                  {total.toLocaleString()}
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={
                                    viewBox.cy +
                                    (chartHeight < 350
                                      ? 14
                                      : chartHeight < 400
                                      ? 16
                                      : 24)
                                  }
                                  className={`fill-muted-foreground ${
                                    chartHeight < 350
                                      ? "text-xs"
                                      : chartHeight < 400
                                      ? "text-xs"
                                      : "text-sm"
                                  }`}
                                >
                                  Total
                                </tspan>
                              </text>
                            )}
                          />
                        </Pie>
                        <Tooltip
                          content={
                            <CustomTooltip
                              nameKey={nameKey}
                              valueKey={valueKey}
                            />
                          }
                        />
                        {chartHeight >= 400 && (
                          <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            wrapperStyle={{
                              maxHeight: `${chartHeight - 100}px`,
                              overflowY: "auto",
                              paddingLeft: "20px",
                              fontSize: chartHeight < 500 ? "12px" : "14px",
                            }}
                          />
                        )}
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </TabsContent>
            )}

            <TabsContent value="bar" className="mt-0">
              <div
                className="overflow-x-auto overflow-y-hidden w-full"
                style={{ height: `${chartHeight}px` }}
              >
                <div
                  className="flex items-center justify-center sm:justify-start h-full"
                  style={{
                    minWidth:
                      Math.max(
                        processedData.length *
                          (chartHeight < 400
                            ? 25
                            : chartHeight < 640
                            ? 30
                            : 40),
                        280
                      ) + "px",
                    height: "100%",
                  }}
                >
                  {/* read desc above pie chart */}
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={processedData}
                      margin={{
                        top: 15,
                        right:
                          chartHeight < 400 ? 5 : chartHeight < 640 ? 20 : 30,
                        bottom:
                          chartHeight < 400 ? 30 : chartHeight < 640 ? 50 : 60,
                        left:
                          chartHeight < 400 ? 5 : chartHeight < 640 ? 20 : 30,
                      }}
                    >
                      {/* the grid that's shown behind the bars, not mandatory to display, but helpful */}
                      <CartesianGrid strokeDasharray="3 3" />

                      <XAxis
                        // the label that's displayed under each bar
                        dataKey={nameKey}
                        // the angle at which the label is displayed, extremely helpful in preventing overlapping
                        angle={-45}
                        textAnchor="end"
                        height={
                          chartHeight < 400 ? 50 : chartHeight < 640 ? 70 : 80
                        }
                        // if we want to not render any labels
                        // can also use a function
                        interval={0}
                        tick={{
                          fontSize:
                            chartHeight < 350 ? 8 : chartHeight < 400 ? 10 : 12,
                        }}
                      />
                      {/* the y axis labels */}
                      <YAxis
                        tick={{
                          fontSize:
                            chartHeight < 350 ? 8 : chartHeight < 400 ? 10 : 12,
                        }}
                      />

                      {/* see pie chart desc */}
                      <Tooltip />

                      {/* to display something under the x-axis*/}
                      {chartHeight >= 400 && <Legend />}

                      {/* to actual bar */}
                      <Bar
                        dataKey={valueKey}
                        fill={colors[0]}
                        radius={[4, 4, 0, 0]}
                        label={
                          chartHeight >= 400
                            ? {
                                position: "top",
                                formatter: (value) => value.toLocaleString(),
                                fontSize: chartHeight < 500 ? 10 : 12,
                              }
                            : false
                        }
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="line" className="mt-0">
              <div
                className="overflow-x-auto overflow-y-hidden w-full"
                style={{ height: `${chartHeight}px` }}
              >
                <div
                  className="flex items-center justify-center sm:justify-start h-full"
                  style={{
                    minWidth:
                      Math.max(
                        processedData.length *
                          (chartHeight < 400
                            ? 25
                            : chartHeight < 640
                            ? 30
                            : 40),
                        280
                      ) + "px",
                    height: "100%",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={lineChartData}
                      margin={{
                        top: 15,
                        right:
                          chartHeight < 400 ? 5 : chartHeight < 640 ? 20 : 30,
                        bottom:
                          chartHeight < 400 ? 30 : chartHeight < 640 ? 50 : 60,
                        left:
                          chartHeight < 400 ? 5 : chartHeight < 640 ? 20 : 30,
                      }}
                    >
                      {/* see bar desc */}
                      <CartesianGrid strokeDasharray="3 3" />
                      {/* see bar desc */}
                      <XAxis
                        dataKey={nameKey}
                        angle={-45}
                        textAnchor="end"
                        height={
                          chartHeight < 400 ? 50 : chartHeight < 640 ? 70 : 80
                        }
                        interval={0}
                        tick={{
                          fontSize:
                            chartHeight < 350 ? 8 : chartHeight < 400 ? 10 : 12,
                        }}
                      />
                      {/* same as above */}
                      <YAxis
                        tick={{
                          fontSize:
                            chartHeight < 350 ? 8 : chartHeight < 400 ? 10 : 12,
                        }}
                      />
                      <Tooltip
                        content={
                          <CustomTooltip
                            nameKey={nameKey}
                            valueKey={valueKey}
                            type="line"
                          />
                        }
                      />
                      {chartHeight >= 400 && <Legend />}
                      <Line
                        type="monotone"
                        dataKey={valueKey}
                        stroke={colors[0]}
                        strokeWidth={chartHeight < 400 ? 1.5 : 2}
                        dot={{ r: chartHeight < 400 ? 2.5 : 4 }}
                        activeDot={{ r: chartHeight < 400 ? 4 : 6 }}
                        label={
                          chartHeight >= 400
                            ? {
                                position: "top",
                                formatter: (value) => value.toLocaleString(),
                                fontSize: chartHeight < 500 ? 10 : 12,
                              }
                            : false
                        }
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </CardContent>

      {(footerTrend || footerText) && (
        <div className="p-3 sm:p-6 pt-0 flex-col gap-2 text-xs sm:text-sm text-center sm:text-left">
          {footerTrend && (
            <div className="flex items-center justify-center sm:justify-start gap-2 font-medium leading-none">
              {footerTrend} <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
            </div>
          )}
          {footerText && (
            <div className="leading-none text-muted-foreground break-words">
              {footerText}
            </div>
          )}
        </div>
      )}
      <Dialog
        open={!!selectedOthers}
        onOpenChange={() => setSelectedOthers(null)}
      >
        <DialogContent className="max-w-xs sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col mx-4">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              Other Items ({selectedOthers?.length})
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Name</TableHead>
                  <TableHead className="text-right text-xs sm:text-sm">
                    Value
                  </TableHead>
                  <TableHead className="text-right text-xs sm:text-sm">
                    Percentage
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedOthers?.map((item, index) => {
                  const percentage = ((item.value / total) * 100).toFixed(2);
                  return (
                    <TableRow key={index}>
                      <TableCell className="text-xs sm:text-sm break-words">
                        {item.name}
                      </TableCell>
                      <TableCell className="text-right text-xs sm:text-sm">
                        {item.value.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-xs sm:text-sm">
                        {percentage}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ChartViewTemplate;
